<template>
  <div class="message-compose" ref="message-thread-form">
    <div v-if="hasUploadFiles" class="upload-file w-100 d-flex border-bottom">
      <div
        v-for="file in uploadedFiles"
        :key="file.signed_id"
        class="upload-file__preview"
      >
        <v-button
          round
          ghost
          class="upload-file__remove p-absolute"
          @click="removeUploadedFile(file)"
        >
          <v-icon icon="cancel" />
        </v-button>
        <template v-if="isVideo(file)">
          <video>
            <source :src="file.preview_url" type="video/mp4" />
            <source :src="file.preview_url" type="video/ogg" />
          </video>
          <v-icon
            size="2.75rem"
            icon="play_arrow"
            class="upload-file__play p-absolute"
          />
        </template>
        <img v-else :src="file.preview_url" />
      </div>
      <div
        v-for="file in uploadingFiles"
        :key="file.key || +new Date()"
        class="upload-file__preview --uploading"
      >
        <video v-if="isVideo(file)">
          <source :src="blobUrl(file)" type="video/mp4" />
          <source :src="blobUrl(file)" type="video/ogg" />
        </video>
        <img v-else :src="blobUrl(file)" />

        <v-button
          v-if="file.failed"
          round
          ghost
          class="upload-file__remove p-absolute"
          @click="removeUploadingFile(file)"
        >
          <v-icon icon="cancel" />
        </v-button>

        <template v-if="file.failed">
          <div class="upload__loader --fail">
            <v-icon icon="error_outline" />
            {{ $t("components.messageThread.messageCompose.failed") }}
          </div>
        </template>

        <template v-else>
          <div class="upload__loader">
            <v-loader />
            <span v-if="loadingMessages[file.key]">
              {{ loadingMessages[file.key] }}
            </span>
          </div>
        </template>
      </div>
    </div>
    <div class="message-compose__control">
      <v-textarea
        ref="textarea"
        :style="{ height: textareaHeight }"
        :placeholder="placeholder"
        v-model="message"
        class="message-compose__text"
        :disabled="isLoading"
        :autofocus="$route.query.focus"
        @focus="handleTextareaFocus"
        @blur="() => $emit('blur')"
        @input="handleTextareaInput"
        @enter="handleTextareaEnter"
      />
      <v-button round ghost class="mr-n1" @click="chooseFile">
        <v-icon icon="image" size="18px" />
      </v-button>
      <v-button round ghost :is-loading="isLoading" @click="submit">
        <v-icon icon="send" size="18px" />
      </v-button>
    </div>
    <input
      class="d-none"
      ref="fileUpload"
      type="file"
      :accept="supportedFileType"
      multiple
      @input="e => addFiles(e)"
    />
  </div>
</template>

<script>
import * as UpChunk from "@mux/upchunk"

import { mapGetters } from "vuex"
import ApiClient from "@/lib/api-client"

import { isBuilderContext, isIos, isMobile } from "@/lib/is-platform"
import { MESSAGE_TYPES } from "@/lib/pusher-utils"
import { isPresent } from "@/lib/utils"
import ImageUpload from "@/lib/mixins/image-upload"
import { SUPPORTED_VIDEO_MIME_TYPES } from "@shared/upload/supported-video-types"
import { VIDEO_SIZE_LIMIT, VIDEO_UPLOAD_STATUS } from "@/lib/upload-helpers"
import { generatedMessagesFromLinkText } from "@/lib/messages-utils"

import vTextarea from "@/components/forms/vTextarea"

const INITIAL_TEXT_AREA_HEIGHT = 44
const INITIAL_TEXT_AREA_HEIGHT_PX = `${INITIAL_TEXT_AREA_HEIGHT}px`

export default {
  mixins: [ImageUpload],

  components: {
    vTextarea
  },

  props: {
    threadId: { type: [Number, String], default: null },
    isLoading: { type: Boolean, default: false },
    placeholder: { type: String, default: "Write something..." }
  },

  data() {
    return {
      message: "",
      loadingMessages: {},
      uploadedImages: [],
      uploadedVideos: [],
      uploadingFiles: [],
      textareaHeight: INITIAL_TEXT_AREA_HEIGHT_PX
    }
  },

  computed: {
    ...mapGetters("messages", ["getReplyingMessage"]),
    ...mapGetters("auth", ["isAuthenticated"]),

    replyingMessage() {
      return this.getReplyingMessage(this.threadId)
    },

    supportedFileType() {
      return "image/*, ".concat(SUPPORTED_VIDEO_MIME_TYPES)
    },

    uploadedFiles() {
      return this.uploadedImages.concat(this.uploadedVideos)
    },

    uploadedImageUrls() {
      return this.uploadedImages.map(file => file.url)
    },

    uploadedImageIds() {
      return this.uploadedImages.map(file => file.signed_id)
    },

    hasUploadFiles() {
      return !!this.uploadedFiles.length || this.hasUploadingFiles
    },

    hasUploadingFiles() {
      return !!this.uploadingFiles.length
    }
  },

  methods: {
    submit() {
      if (isBuilderContext()) return
      if (!this.isAuthenticated) return

      let messages = []
      const createdAt = +new Date()

      if (this.message.length > 0) {
        messages = generatedMessagesFromLinkText({
          text: this.message,
          createdAt
        })
      }

      if (this.uploadedImages.length > 0) {
        messages.push({
          imageUrls: this.uploadedImageUrls,
          imageIds: this.uploadedImageIds,
          messageType: MESSAGE_TYPES.IMAGE,
          messageCreatedAt: createdAt + messages.length
        })
      }

      this.uploadedVideos.forEach(video => {
        messages.push({
          videoUrl: video.url,
          messageType: MESSAGE_TYPES.VIDEO,
          messageCreatedAt: createdAt + messages.length
        })
      })

      if (messages.length > 0) {
        this.$emit("submit", messages)
        this.resetForm()
      }
    },

    isVideo(file) {
      return file.type.startsWith("video/")
    },

    isImage(file) {
      return file.type.startsWith("image/")
    },

    handleTextareaInput(value, el) {
      this.$nextTick(() => {
        this.textareaHeight =
          (value
            ? Math.max(el.scrollHeight, INITIAL_TEXT_AREA_HEIGHT)
            : INITIAL_TEXT_AREA_HEIGHT) + "px"
      })
      this.$emit("resetTimer")
    },

    handleTextareaFocus() {
      this.$emit("focus")
      this.$emit("click") // Required for FB Livestream mobile
      this.$emit("resetTimer")

      if (!this.isAuthenticated) {
        this.$router.push("#auth")
      }
    },

    handleTextareaEnter(e) {
      if (isMobile() || isIos()) {
        return
      } else {
        e.preventDefault()
        this.submit()
      }
    },

    resetForm() {
      this.message = ""
      this.uploadedImages = []
      this.uploadedVideos = []
      this.textareaHeight = INITIAL_TEXT_AREA_HEIGHT_PX
      this.$emit("resetForm")
    },

    chooseFile() {
      this.$refs.fileUpload.click()
    },

    async addFiles(e) {
      if (isBuilderContext()) return

      const files = Object.values(e.target.files)
      let validFiles = []

      this.resetUploadInput()

      // Validate files
      files.forEach((file, index) => {
        // TODO: extract video validation & upload to library
        if (this.isVideo(file)) {
          if (file.size > VIDEO_SIZE_LIMIT) {
            this.showError(file, "Video too large. Max size 50mb")
            return
          }
        } else {
          const validation = this.validateFile(file)

          if (!validation.valid) {
            this.showError(file, validation.errorMessage)
            return
          }
        }
        file.key = +new Date() + index
        validFiles.push(file)
      })

      // Handle upload valid files
      this.uploadingFiles.push(...validFiles)

      for (const file of validFiles) {
        try {
          if (this.isVideo(file)) {
            this.uploadVideo(file)
          } else {
            const uploadedImage = await this.uploadFile(file)

            this.uploadedImages.push({
              type: "image/",
              url: uploadedImage.path,
              signed_id: uploadedImage.signed_id,
              preview_url: this.blobUrl(file)
            })
            this.removeUploadingFile(file)
          }
        } catch {
          this.markErrorForUploadingFile(file)
        }
      }
    },

    uploadVideo(file) {
      this.$set(this.loadingMessages, file.key, "Uploading")

      return ApiClient.post(`/threads/uploads`, {
        filename: file.name
      })
        .then(res => {
          const upload = UpChunk.createUpload({
            endpoint: res.data.presigned_url,
            file: file
          })

          upload.on("success", () =>
            this.ensureVideoPrepared(res.data.upload_id, file)
          )

          upload.on("progress", progress => {
            if (progress.detail === 100) {
              this.$set(this.loadingMessages, file.key, "Preparing")
            } else {
              this.$set(
                this.loadingMessages,
                file.key,
                `${Math.round(progress.detail)}%`
              )
            }
          })
        })
        .catch(() => {
          this.markErrorForUploadingFile(file)
        })
    },

    markErrorForUploadingFile(file) {
      let index = this.uploadingFiles.findIndex(f => f == file)
      let fileToUpdate = this.uploadingFiles[index]
      fileToUpdate.failed = true

      this.$set(this.uploadingFiles, index, fileToUpdate)

      if (file.key) {
        this.$set(this.loadingMessages, file.key, "Failed")
      }
      this.showError(file)
    },

    ensureVideoPrepared(uploadId, file) {
      try {
        ApiClient.get(`/threads/uploads/${uploadId}`)
          .then(({ data }) => {
            if (isPresent(data.errors)) {
              this.markErrorForUploadingFile(file)
              return
            }

            if (data.status === VIDEO_UPLOAD_STATUS.PREPARING) {
              setTimeout(() => {
                this.ensureVideoPrepared(uploadId, file)
              }, 1000)

              return
            }

            this.uploadedVideos.push({
              type: "video/",
              url: data.video_url,
              signed_id: data.video_id,
              preview_url: this.blobUrl(file)
            })
            this.removeUploadingFile(file)
          })
          .catch(() => {
            this.markErrorForUploadingFile(file)
          })
      } catch (e) {
        this.markErrorForUploadingFile(file)
      }
    },

    showError(file, message = "Couldn't upload") {
      this.$notify(`${message}: ${file.name}`)
    },

    removeUploadingFile(file) {
      this.loadingMessages[file.key] = "Uploading"
      this.uploadingFiles = this.uploadingFiles.filter(f => f != file)
    },

    removeUploadedFile(file) {
      if (this.isVideo(file)) {
        this.uploadedVideos = this.uploadedVideos.filter(f => f.key != file.key)
      } else if (this.isImage(file)) {
        this.uploadedImages = this.uploadedImages.filter(
          f => f.signed_id != file.signed_id
        )
      }
    },

    resetUploadInput() {
      this.$refs.fileUpload.value = ""
    }
  },

  watch: {
    isLoading: {
      immediate: true,
      handler() {
        if (!this.isLoading && this.$route.query.focus) {
          this.$nextTick(() => {
            this.$refs["textarea"].$el.focus()
          })
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.message-compose {
  .upload-file {
    @include hide-scroll-bars;

    padding: 9px 11px 6px;
    overflow-x: scroll;

    &__preview {
      margin-right: 1rem;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 0 0 auto;

      img {
        max-width: 250px;
      }

      &.--uploading {
        img,
        video {
          opacity: 0.5;
        }
      }

      img,
      video {
        height: $message-file-upload-preview--height;
        border-radius: 6px;
        object-fit: cover;
      }
    }

    &__remove {
      top: -0.75rem;
      right: -0.75rem;
      width: 2rem;
      height: 2rem;
      min-width: 2rem;
      min-height: 2rem;
      color: $color-ui--black;
      z-index: 10;

      .v-icon {
        background-color: $color-ui--white;
        border-radius: 50%;
      }
    }

    &__play {
      background-color: rgb($color-ui--black, 0.2);
      height: 100%;
      width: 100%;
      left: 0;
      top: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $color-ui--white;
      border-radius: px;
    }
  }

  .message-compose__control {
    display: flex;
    align-items: center;
  }

  .v-textarea.message-compose__text {
    flex-grow: 1;
    font-family: $system-font-stack;
    font-size: 1rem;
    resize: none;
    transition: background 0.2s ease, height 0.2s ease;
    min-height: $size--4;
    height: $thread--text-box-height;
    max-height: $thread--text-box-max-height;
    border-color: transparent;

    &:focus {
      border-color: transparent;
    }
  }

  .message-compose__button {
    background: none;
    border: none;
    padding-left: 1rem;
  }

  .upload__loader {
    background: $color-ui--white;
    border-radius: $size--2;
    position: absolute;
    left: 2px;
    bottom: 2px;
    height: 18px;
    padding: 0.25rem;
    box-shadow: $box-shadow--small;
    display: flex;
    align-items: center;
    font-size: $font-size--x-small;
    color: $color-ui--grey-70;
    font-weight: $font-weight--semi-bold;

    .v-loader {
      height: 14px;
      width: 14px;
      border-width: 2px;
    }

    span {
      margin-left: 0.25rem;
    }

    &.--fail {
      color: $color-ui--red;

      .v-icon {
        font-size: 1rem;
        margin-left: 0;
        margin-right: 0.25rem;
      }
    }
  }
}
</style>
